import React, { useRef, useEffect } from 'react';
import { FREE_KUNDLI_TRANSLATIONS } from '../../../../languages/freeKundli';

interface PlanetData {
    planet: string;
    house: number | string;
    rashi: string;
    longitude: string;
    nakshatra: string;
    pada: number | string;
}

interface KundliChartProps {
    planets: PlanetData[];
    language?: 'english' | 'hindi';
    // Height as a fraction of width. 1 = square (default, free-kundli grids);
    // ~0.66 gives the classic landscape North Indian chart proportions.
    aspectRatio?: number;
}

// Rashi to house number mapping (Rashi Chart style)
const RASHI_MAP: { [key: string]: number } = {
    // English names
    'Aries': 1, 'Taurus': 2, 'Gemini': 3, 'Cancer': 4, 'Leo': 5, 'Virgo': 6,
    'Libra': 7, 'Scorpio': 8, 'Sagittarius': 9, 'Saggitarius': 9, 'Capricorn': 10, 'Aquarius': 11, 'Pisces': 12,
    // Sanskrit names (from backend)
    'Mesha': 1, 'Vrishabha': 2, 'Vrushaba': 2, 'Mithuna': 3, 'Karka': 4, 'Simha': 5, 'Kanya': 6,
    'Tula': 7, 'Vrischika': 8, 'Vrishchika': 8, 'Vruschika': 8, 'Dhanu': 9, 'Makara': 10, 'Kumbha': 11, 'Meena': 12,
    // Hindi names
    'मेष': 1, 'वृषभ': 2, 'मिथुन': 3, 'कर्क': 4, 'सिंह': 5, 'कन्या': 6,
    'तुला': 7, 'वृश्चिक': 8, 'धनु': 9, 'मकर': 10, 'कुम्भ': 11, 'मीन': 12
};

// Planet abbreviations - now language aware
function getPlanetAbbr(name: string, language: 'english' | 'hindi' = 'english'): string {
    const langKey = language === 'hindi' ? 'Hindi' : 'English';
    const t = FREE_KUNDLI_TRANSLATIONS[langKey];

    // Try to get abbreviation from translations first
    if (t.planetAbbr?.[name]) {
        return t.planetAbbr[name];
    }

    // Fallback to built-in abbreviations
    const abbr: { [key: string]: string } = {
        // English names
        'Sun': language === 'hindi' ? 'सू' : 'Su',
        'Moon': language === 'hindi' ? 'चं' : 'Mo',
        'Mars': language === 'hindi' ? 'मं' : 'Ma',
        'Mercury': language === 'hindi' ? 'बु' : 'Me',
        'Jupiter': language === 'hindi' ? 'गु' : 'Ju',
        'Venus': language === 'hindi' ? 'शु' : 'Ve',
        'Saturn': language === 'hindi' ? 'श' : 'Sa',
        'Rahu': language === 'hindi' ? 'रा' : 'Ra',
        'Ketu': language === 'hindi' ? 'के' : 'Ke',
        'North Node': language === 'hindi' ? 'रा' : 'Ra',
        'South Node': language === 'hindi' ? 'के' : 'Ke',
        'North Node (Rahu)': language === 'hindi' ? 'रा' : 'Ra',
        'South Node (Ketu)': language === 'hindi' ? 'के' : 'Ke',
        'Uranus': language === 'hindi' ? 'अ' : 'Ur',
        'Neptune': language === 'hindi' ? 'व' : 'Ne',
        'Pluto': language === 'hindi' ? 'य' : 'Pl',
        // Backend short names
        'Merc': language === 'hindi' ? 'बु' : 'Me',
        'Jupt': language === 'hindi' ? 'गु' : 'Ju',
        'Venu': language === 'hindi' ? 'शु' : 'Ve',
        'Satn': language === 'hindi' ? 'श' : 'Sa',
        // Hindi input names (from backend)
        'सूर्य': 'सू', 'चंद्र': 'चं', 'चन्द्र': 'चं', 'मंगल': 'मं', 'बुध': 'बु',
        'बृहस्पति': 'गु', 'गुरु': 'गु', 'शुक्र': 'शु', 'शनि': 'श', 'राहु': 'रा', 'केतु': 'के',
        'लग्न': 'ल', 'Ascendant': language === 'hindi' ? 'ल' : 'As'
    };
    return abbr[name] || (name ? name.substring(0, 2) : '');
}

// Planet colors (tuned for glass background with better contrast)
function getPlanetColor(name: string): string {
    const palette: { [key: string]: string } = {
        // English names - brighter colors for better visibility
        Sun: '#FCD34D', Moon: '#E0F2FE', Mars: '#FCA5A5', Mercury: '#6EE7B7',
        Jupiter: '#FDE047', Venus: '#F9A8D4', Saturn: '#C4B5FD', Rahu: '#D1D5DB',
        Ketu: '#FDBA74', Uranus: '#A5F3FC', Neptune: '#93C5FD', Pluto: '#F0ABFC',
        'North Node': '#D1D5DB', 'South Node': '#FDBA74',
        'North Node (Rahu)': '#D1D5DB', 'South Node (Ketu)': '#FDBA74',
        // Backend short names
        Merc: '#6EE7B7', Jupt: '#FDE047', Venu: '#F9A8D4', Satn: '#C4B5FD',
        Uran: '#A5F3FC', Nept: '#93C5FD', Plut: '#F0ABFC',
        // Hindi names
        'सूर्य': '#FCD34D', 'चंद्र': '#E0F2FE', 'मंगल': '#FCA5A5', 'बुध': '#6EE7B7',
        'गुरु': '#FDE047', 'शुक्र': '#F9A8D4', 'शनि': '#C4B5FD', 'राहु': '#D1D5DB',
        'केतु': '#FDBA74', 'अरुण': '#A5F3FC', 'वरुण': '#93C5FD', 'यम': '#F0ABFC'
    };
    return palette[name] || 'rgba(255,255,255,0.95)';
}

// House geometry in percentage coordinates: polygon vertices, planet-block
// center (cx, cy) and house-number corner anchor (nx, ny).
const HOUSES: { num: number; poly: [number, number][]; cx: number; cy: number; nx: number; ny: number }[] = [
    { num: 1, poly: [[50, 0], [75, 25], [50, 50], [25, 25]], cx: 50, cy: 25, nx: 50, ny: 45 },
    { num: 2, poly: [[0, 0], [50, 0], [25, 25]], cx: 25, cy: 10, nx: 25, ny: 21 },
    { num: 3, poly: [[0, 0], [25, 25], [0, 50]], cx: 9, cy: 25, nx: 20, ny: 25 },
    { num: 4, poly: [[25, 25], [50, 50], [25, 75], [0, 50]], cx: 25, cy: 50, nx: 45, ny: 50 },
    { num: 5, poly: [[0, 50], [25, 75], [0, 100]], cx: 9, cy: 75, nx: 20, ny: 75 },
    { num: 6, poly: [[25, 75], [50, 100], [0, 100]], cx: 25, cy: 90, nx: 25, ny: 79 },
    { num: 7, poly: [[50, 50], [75, 75], [50, 100], [25, 75]], cx: 50, cy: 75, nx: 50, ny: 55 },
    { num: 8, poly: [[75, 75], [100, 100], [50, 100]], cx: 75, cy: 90, nx: 75, ny: 79 },
    { num: 9, poly: [[75, 75], [100, 50], [100, 100]], cx: 91, cy: 75, nx: 80, ny: 75 },
    { num: 10, poly: [[75, 25], [100, 50], [75, 75], [50, 50]], cx: 75, cy: 50, nx: 55, ny: 50 },
    { num: 11, poly: [[100, 0], [100, 50], [75, 25]], cx: 91, cy: 25, nx: 80, ny: 25 },
    { num: 12, poly: [[50, 0], [100, 0], [75, 25]], cx: 75, cy: 10, nx: 75, ny: 21 },
];

// Ray-casting point-in-polygon test in percentage coordinates
const pointInPoly = (px: number, py: number, poly: [number, number][]): boolean => {
    let inside = false;
    for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
        const [xi, yi] = poly[i];
        const [xj, yj] = poly[j];
        if ((yi > py) !== (yj > py) && px < ((xj - xi) * (py - yi)) / (yj - yi) + xi) {
            inside = !inside;
        }
    }
    return inside;
};

const KundliChart: React.FC<KundliChartProps> = ({ planets, language = 'english', aspectRatio = 1 }) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const hoveredHouseRef = useRef<number | null>(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        const container = containerRef.current;
        if (!canvas || !container) return;

        const drawChart = () => {
            const containerWidth = container.offsetWidth;
            if (containerWidth === 0) return; // Wait for visibility

            const size = Math.min(containerWidth, 500);
            const height = Math.round(size * aspectRatio);

            // High DPI support
            const dpr = window.devicePixelRatio || 1;
            canvas.width = size * dpr;
            canvas.height = height * dpr;
            canvas.style.width = `${size}px`;
            canvas.style.height = `${height}px`;

            const ctx = canvas.getContext('2d');
            if (!ctx) return;

            ctx.scale(dpr, dpr);
            ctx.clearRect(0, 0, size, height);

            const w = size;
            const h = height;

            // Calculate planets in houses based on rashi
            const housePlanets: { label: string; color: string }[][] = Array.from({ length: 13 }, () => []);

            if (planets && planets.length > 0) {
                planets.forEach((planet) => {
                    const planetRashi = planet.rashi;
                    // Use the house number directly if available, otherwise fallback to rashi mapping
                    // Ensure houseNum is a valid integer between 1 and 12
                    let houseNum = parseInt(String(planet.house), 10);

                    if (isNaN(houseNum) || houseNum < 1 || houseNum > 12) {
                        houseNum = RASHI_MAP[planetRashi] || 1;
                    }

                    const shortName = getPlanetAbbr(planet.planet, language);
                    const color = getPlanetColor(planet.planet);

                    if (housePlanets[houseNum]) {
                        housePlanets[houseNum].push({ label: shortName, color: color });
                    }
                });
            }

            // Drawing helper: traces the house polygon, fills it when hovered
            const drawHousePoly = (poly: [number, number][], hovered: boolean) => {
                ctx.beginPath();
                poly.forEach(([px, py], i) => {
                    if (i === 0) ctx.moveTo(px * w / 100, py * h / 100);
                    else ctx.lineTo(px * w / 100, py * h / 100);
                });
                ctx.closePath();
                if (hovered) {
                    ctx.fillStyle = 'rgba(192, 132, 252, 0.18)';
                    ctx.fill();
                }
                ctx.lineWidth = 2;
                ctx.strokeStyle = hovered ? 'rgba(216, 180, 254, 1)' : 'rgba(192, 132, 252, 0.8)';
                ctx.stroke();
            };

            // Scale typography down proportionally when the chart renders small
            // (e.g. the compact preview in the chat profile popover) so labels
            // fit inside their houses instead of overflowing.
            const fontScale = Math.max(0.55, Math.min(1, size / 350));

            // House number is drawn at the house's inner corner (nx, ny) so the
            // full center of the house stays free for planet rows — houses with
            // many planets can stack extra rows without overflowing.
            const setText = (x: number, y: number, nx: number, ny: number, maintext: string, subtext: { label: string; color: string }[]) => {
                const cx = x * w / 100;
                const cy = y * h / 100;

                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';

                // Calculate rows for planets
                let rows: { label: string; color: string }[][] = [];
                const fontSize = Math.round(13 * fontScale);
                const lineHeight = fontSize + 4;

                if (subtext && subtext.length > 0) {
                    const maxPerRow = 2;
                    for (let i = 0; i < subtext.length; i += maxPerRow) {
                        rows.push(subtext.slice(i, i + maxPerRow));
                    }
                }

                // 1. Draw House Number at the inner corner
                ctx.font = `bold ${Math.round(11 * fontScale)}px Arial`;
                ctx.lineWidth = 3;
                ctx.strokeStyle = "rgba(0,0,0,0.4)";
                ctx.strokeText(maintext, nx * w / 100, ny * h / 100);
                ctx.fillStyle = "rgba(255, 255, 255, 0.6)";
                ctx.fillText(maintext, nx * w / 100, ny * h / 100);

                // 2. Draw Planets centered in the house
                if (rows.length > 0) {
                    const totalHeight = rows.length * lineHeight;
                    const planetsStartY = cy - (totalHeight / 2);

                    rows.forEach((row, rowIndex) => {
                        // Center the row vertically in its line slot
                        // slot top = planetsStartY + (rowIndex * lineHeight)
                        // text mid = slot top + (lineHeight / 2)
                        const rowY = planetsStartY + (rowIndex * lineHeight) + (lineHeight / 2) - 2; // -2 adjustment for visual balance

                        ctx.font = `bold ${fontSize}px Arial`;

                        // Calculate row width to center horizontally
                        let rowWidth = 0;
                        row.forEach((t, i) => {
                            rowWidth += ctx.measureText(t.label).width;
                            if (i < row.length - 1) rowWidth += ctx.measureText(' ').width;
                        });

                        let drawX = cx - (rowWidth / 2);

                        row.forEach((t, i) => {
                            const color = t.color || "rgba(255,255,255,0.95)";

                            ctx.strokeStyle = "rgba(0,0,0,0.8)";
                            ctx.lineWidth = 2.5;
                            ctx.lineJoin = 'round';
                            ctx.strokeText(t.label, drawX, rowY);
                            ctx.fillStyle = color;
                            ctx.fillText(t.label, drawX, rowY);

                            drawX += ctx.measureText(t.label).width;

                            if (i < row.length - 1) {
                                drawX += ctx.measureText(' ').width;
                            }
                        });
                    });
                }
            };

            // Draw all 12 houses; the hovered one gets a soft highlight fill
            HOUSES.forEach((house) => {
                drawHousePoly(house.poly, hoveredHouseRef.current === house.num);
                setText(house.cx, house.cy, house.nx, house.ny, String(house.num), housePlanets[house.num]);
            });
        };

        drawChart();

        const handleMouseMove = (e: MouseEvent) => {
            const rect = canvas.getBoundingClientRect();
            if (rect.width === 0 || rect.height === 0) return;
            const px = ((e.clientX - rect.left) / rect.width) * 100;
            const py = ((e.clientY - rect.top) / rect.height) * 100;
            const hovered = HOUSES.find((house) => pointInPoly(px, py, house.poly))?.num ?? null;
            if (hovered !== hoveredHouseRef.current) {
                hoveredHouseRef.current = hovered;
                canvas.style.cursor = hovered ? 'pointer' : 'default';
                drawChart();
            }
        };

        const handleMouseLeave = () => {
            if (hoveredHouseRef.current !== null) {
                hoveredHouseRef.current = null;
                canvas.style.cursor = 'default';
                drawChart();
            }
        };

        canvas.addEventListener('mousemove', handleMouseMove);
        canvas.addEventListener('mouseleave', handleMouseLeave);

        const observer = new ResizeObserver(() => {
            drawChart();
        });

        observer.observe(container);

        return () => {
            canvas.removeEventListener('mousemove', handleMouseMove);
            canvas.removeEventListener('mouseleave', handleMouseLeave);
            observer.disconnect();
        };

    }, [planets, language, aspectRatio]);

    return (
        <div
            ref={containerRef}
            className="w-full max-w-[500px] mx-auto flex justify-center items-center p-4"
        >
            <canvas
                ref={canvasRef}
                className="border border-white/20 rounded-lg"
                style={{ maxWidth: '100%', height: 'auto' }}
            />
        </div>
    );
};

export default KundliChart;
